import PropTypes from "prop-types";
import { useSelector } from "react-redux";

/**
 * "polyfill" for toFixed since it breaks in some JS versions.
 * For exmaple: (14.995).toFixed(2) becomes 14.99 instead of 15.
 * https://www.sitepoint.com/number-tofixed-rounding-errors-broken-but-fixable/
 *
 * @param {number} n - number to be fixed
 * @param {number} d - number of decimals
 * @param {string} s - separator type
 */
export const toFixedCurrency = (n, d, separator = ",") => {
    // eslint-disable-next-line eqeqeq
    if ((1.005).toFixed(2) == "1.01") {
        return n.toFixed(d);
    }
    const split = n.toString().split(".");

    if (split[1] && split[1].length === 1) {
        split[1] += "0";
    }

    const number = +(!split[1] ? split[0] : `${split.join(".")}1`);
    return number.toFixed(d).replace(".", separator);
};

const memo = {};

/**
 * Returns formatted price that auto adds vat
 *
 * @param {boolean} [isRounded=false] - If price should be rounded or not.
 * @param {string} [separateWith] - String that will separate number, ex: 1.000 if '.', 1 000 if ' '
 * @param {number} price - Price excluding VAT
 * @param {func} [render] - Render func with formatted price as prop
 * @param {number} [vatRate=1] Price is multiplied with this if showVat is set.
 * @param {number} [giftCardAmount=0] - Amount to be deducted by giftcard.
 * @param {boolean} [sendPriceAsNumber=false] - Will return object with price as int and string if true. Will only return string if false.
 * @return {string|object}
 *
 * @example
 *
 *      <FormatPrice
 *          isRounded
 *          separateWith="."
 *          price={totalPricing.priceExVat}
 *          vatRate={totalPricing.vatRate}
 *          render=(price => t('price_with_currency', { price }))
 *      />
 */

const FormatPrice = ({ isRounded, separateWith, price, render, vatRate, giftCardAmount, sendPriceAsNumber }) => {
    const currencyCode = useSelector(({ application }) => application.currency.code);
    const locale = useSelector(({ application }) => application.locale);
    const isEUR = currencyCode === "EUR";
    // The English sites write 1,234.50, the Swedish one 1 234,50.
    const isEnglish = !isEUR && /^en/i.test(locale || "");
    // The server renders every market, so the market is part of the key.
    const key = `${locale}_${currencyCode}_${+isRounded}${separateWith || ""}_${price}_${vatRate}_${giftCardAmount}`;
    const totalPrice = price * vatRate - giftCardAmount;

    if (!memo[key]) {
        if (isEUR) {
            separateWith = "\u00A0";
        } else if (isEnglish && separateWith) {
            separateWith = ",";
        }

        let p;
        if (Number.isInteger(totalPrice)) {
            p = toFixedCurrency(totalPrice, 0, ".");
        } else {
            p = toFixedCurrency(totalPrice, 2, ".");
        }

        const splitPrice = p.toString().split(".");
        if (separateWith) {
            splitPrice[0] = splitPrice[0].replace(/\B(?=(\d{3})+(?!\d))/g, separateWith);
        }
        p = splitPrice.join(isEnglish ? "." : ",");

        memo[key] = p;
    }

    const memoizedPrice = memo[key];

    if (sendPriceAsNumber) {
        return render ? render(memoizedPrice, totalPrice) : { price, priceAsNumber: totalPrice };
    } else {
        return render ? render(memoizedPrice) : memoizedPrice;
    }
};

FormatPrice.propTypes = {
    giftCardAmount: PropTypes.number,
    isRounded: PropTypes.bool,
    price: PropTypes.number.isRequired,
    render: PropTypes.func,
    separateWith: PropTypes.string,
    vatRate: PropTypes.number,
    sendPriceAsNumber: PropTypes.bool,
};

FormatPrice.defaultProps = {
    giftCardAmount: 0,
    isRounded: false,
    render: null,
    separateWith: null,
    vatRate: 1,
    sendPriceAsNumber: false,
};

export default FormatPrice;
